/**
 * The **hit flash** for Arena enemy hit reactions (PRD #430 Slice A, ADR-0225):
 * every skill hit flashes the struck enemy white for an instant, so a volley
 * into the horde reads as a wall of impact rather than silent damage numbers.
 *
 * The pure per-sprite flash machine (the {@link ScreenShake} pattern: pure math
 * over its own state, time injected via `update(dtMs)` - no Pixi, no wall clock,
 * no randomness - so the curve is node-testable). One machine is pooled per
 * struck entity by the renderer and reused across hits: `trigger()` restarts it
 * (a retrigger never stacks), `intensity()` reads the curve, and once expired it
 * reports {@link idle} so the renderer skips it entirely.
 *
 * The curve: full white ({@link intensity} 1) for {@link HIT_FLASH_HOLD_MS},
 * then a linear decay to 0 over {@link HIT_FLASH_DECAY_MS} - ~170ms total, long
 * enough to read per enemy, short enough that a dense cascade ripples rather
 * than strobes.
 *
 * The *rendering* of the intensity is the renderer's side (see
 * `gameRenderer.ts` + `whitenFilter.ts`): a multiplicative `tint` can only
 * darken the baked art, so whitening goes through a small pool of shared
 * mix-toward-white filters, one per quantised {@link hitFlashLevel} step. The
 * quantisation is what makes the pool shareable: every sprite at the same step
 * points at the same filter instance, so a 150-enemy wave allocates nothing per
 * hit and at most {@link HIT_FLASH_LEVELS} filters per tab, ever.
 */

/** How long the flash holds full white after a hit, ms. */
export const HIT_FLASH_HOLD_MS = 50;

/** How long the flash decays from full white back to nothing, ms. */
export const HIT_FLASH_DECAY_MS = 120;

/** The whole flash window (hold + decay): past this the machine is idle. */
export const HIT_FLASH_TOTAL_MS = HIT_FLASH_HOLD_MS + HIT_FLASH_DECAY_MS;

/**
 * How many discrete whiten steps the shared filter pool carries. Four steps
 * across a 120ms decay (~7 frames at 60fps) is visually smooth for a 100%->0%
 * fade this fast, and keeps the pool tiny.
 */
export const HIT_FLASH_LEVELS = 4;

/**
 * Quantise a flash intensity (0..1) to a shared-filter pool level: 0 = no
 * filter, 1..{@link HIT_FLASH_LEVELS} = the pooled filter at `level / LEVELS`
 * whiteness. Any positive intensity maps to at least level 1 (the tail of the
 * decay stays visible until it truly expires), and over-range clamps.
 */
export function hitFlashLevel(intensity: number): number {
  if (intensity <= 0) return 0;
  return Math.min(HIT_FLASH_LEVELS, Math.max(1, Math.ceil(intensity * HIT_FLASH_LEVELS)));
}

export class HitFlash {
  /** Elapsed ms since the last trigger; `Infinity` = never triggered / expired-idle. */
  private elapsed = Infinity;

  /** (Re)start the flash at full white. A retrigger restarts - it never stacks. */
  trigger(): void {
    this.elapsed = 0;
  }

  /** Advance the flash by one render frame. `dtMs <= 0` (or idle) holds steady. */
  update(dtMs: number): void {
    if (dtMs <= 0 || this.idle) return;
    this.elapsed += dtMs;
  }

  /** Whether the flash is spent (or never triggered) - the renderer skips it. */
  get idle(): boolean {
    return this.elapsed >= HIT_FLASH_TOTAL_MS;
  }

  /** This frame's whiteness in [0, 1]: 1 through the hold, then a linear decay to 0. */
  intensity(): number {
    if (this.idle) return 0;
    if (this.elapsed <= HIT_FLASH_HOLD_MS) return 1;
    return 1 - (this.elapsed - HIT_FLASH_HOLD_MS) / HIT_FLASH_DECAY_MS;
  }
}
